/* eslint-disable react-hooks/exhaustive-deps */
import { CommandBar, DetailsListLayoutMode, Dropdown, IColumn, ICommandBarItemProps, Icon, IconButton, IDetailsColumnRenderTooltipProps, IDetailsHeaderProps, IGroup, IRenderFunction, Label, MessageBar, MessageBarType, ProgressIndicator, SearchBox, SelectionMode, ShimmeredDetailsList, Stack, Sticky, Toggle, TooltipHost } from '@fluentui/react';
import React, { ChangeEvent, useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { getAccreditationRuleDefinitionLabel, getListOfCriteriaDefinitions } from '../data/accreditorRules';
import { getCourse } from '../data/courses';
import { getSemesterFromSisCode, isCanvasStudent, isCanvasTeacher, isCourseGraduateLevel, isMyCourse, sendApiRequest, styles } from '../lib/helper';
import { useAccreditationRulesContext } from '../providers/accreditationrulesprovider';
import { useAuthenticationContext } from '../providers/authenticationprovider';
import { useCoursesContext } from '../providers/coursesprovider';
import { useSemesterContext } from '../providers/semesterprovider';
import { IAccreditorRuleFieldDefinition, ICriteria } from '../types/IAccreditor';
import { ICourse, ISemester } from '../types/ICourse';
import AccreditationRulesPanel from './accreditationRulesPanel';
import { LeftNav } from './layout/leftnav';
import { sortSemesters } from '../data/semesters';
import { debounce } from "@microsoft/sp-lodash-subset";

const Courses: React.FunctionComponent = () => {

  const fakeCourse: ICourse = {
    Course: "",
    BlobFolderName: "",
    Department: "",
    Instructor: "",
    SIS: "",
    Year: 9999,
    Semester: "Fall",
    IsMyCourse: false,
    Title: "",
    SyllabusBody: ""
  }

  const max = 10;
  let fakeCourses: ICourse[] = [];
  for (let i = 0; i < max; i++) {
    fakeCourses.push(fakeCourse);
  }

  const authContext = useAuthenticationContext();
  const coursesContext = useCoursesContext();
  const semesterContext = useSemesterContext();
  const accreditorRulesContext = useAccreditationRulesContext();
  const [filteredCourses, setFilteredCourses] = useState<ICourse[]>(fakeCourses);
  const [searchText, setSearchText] = useState<string>("");
  const [selectedInstructor, setSelectedInstructor] = useState<string | undefined>();
  const [selectedMyCourses, setSelectedMyCourses] = useState<boolean>();
  const [showOnlyGraduateCourses, setShowOnlyGraduateCourses] = useState<boolean>();
  const [showRulesPanelItem, setShowRulesPanelItem] = useState<ICourse | undefined>();
  const [accreditorCriteriaDefinitions, setAccreditorCriteriaDefinitions] = React.useState<ICriteria[]>([]);
  const [navigatingDirectlyToCourse, setNavigatingDirectlyToCourse] = React.useState(false);
  const refreshFlag = useRef(false);

  const navigate = useNavigate();

  useEffect(() => {
    if (semesterContext.SelectedSemester) {
      coursesContext.setSemester(semesterContext.SelectedSemester);
    }
    coursesContext.Result && setFilteredCourses(coursesContext.Result);
  }, [coursesContext, semesterContext]);

  useEffect(() => {
    getListOfCriteriaDefinitions().then(res => {
      setAccreditorCriteriaDefinitions(res);
    })
    if (!(window as any).isAlreadyLaunched) {
      if (authContext.Result?.CanvasAuthStatus?.sisCourseId && authContext.Result.CanvasAuthStatus.sisCourseId !== "undefined") {
        setNavigatingDirectlyToCourse(true);
        console.info(`Attempting to get course via sisCourseId: ${authContext.Result.CanvasAuthStatus.sisCourseId}`)
        const semester = getSemesterFromSisCode(authContext.Result.CanvasAuthStatus.sisCourseId);
        console.info("Parsed semester:", semester);
        if (semester) {
          console.info("Found semester. Attempting to get course by sisCourseId", semester);
          getCourse(authContext.Result.CanvasAuthStatus.sisCourseId, authContext.Result.CanvasAuthStatus.courseId, semester, authContext.Result).then(course => {
            if (course) {
              navigate(`/courses/${course.Year}/${course.Semester}/${encodeURIComponent(course.SIS || course.Course)}`);
              authContext.ClearCurrentCanvasCourseFromAuthContext();
              setNavigatingDirectlyToCourse(false);
            }
          }).catch(err => {
            console.info("Unable to redirect to course", err);
          }).finally(() => {
            setNavigatingDirectlyToCourse(false);
          })
        }
        else
        {
          setNavigatingDirectlyToCourse(false);
        }
      }
      else if (authContext.Result?.CanvasAuthStatus?.courseId && !isNaN(authContext.Result.CanvasAuthStatus.courseId)) {
        console.info(`Attempting get course via courseId: ${authContext.Result.CanvasAuthStatus.courseId}`)
        setNavigatingDirectlyToCourse(true);
        sendApiRequest("/api/canvas/getListOfSemestersFromCanvas", "GET").then(semesters => {
          semesters = semesters.sort(sortSemesters)
          const semester = semesters[0];
          console.info(`Looking for course ${authContext.Result?.CanvasAuthStatus?.courseId} in current semester semester`, semester);
          if (semester && authContext?.Result?.CanvasAuthStatus?.courseId !== undefined) {
            console.info("Found semester. Attempting to get course by courseId", semester, authContext.Result?.CanvasAuthStatus?.courseId);
            getCourse(authContext.Result.CanvasAuthStatus.courseId!.toString(), authContext.Result.CanvasAuthStatus.courseId!.toString(), semester, authContext.Result).then(course => {
              if (course) {
                navigate(`/courses/${course.Year}/${course.Semester}/${encodeURIComponent(course.SIS || course.Course)}`);
                authContext.ClearCurrentCanvasCourseFromAuthContext();
                setNavigatingDirectlyToCourse(false);
              }
              else
              {
                setNavigatingDirectlyToCourse(false);
              }
            }).catch(err => {
              console.info("Unable to redirect to course b/c unable to find course", err);
            }).finally(() => {
              setNavigatingDirectlyToCourse(false);
            })
          }
        }).catch(err => {
          console.info("Unable to redirect to course because unable to get semesters from Canvas", err);
        }).finally(() => {
          setNavigatingDirectlyToCourse(false);
        })
      }
    }
    (window as any).isAlreadyLaunched = true;
  }, [authContext.Result?.CanvasAuthStatus?.sisCourseId])

  const _shouldRenderAccreditingColumns = accreditorRulesContext.Result && accreditorRulesContext.Result.length > 0 && accreditorRulesContext.Result.filter(ar => (coursesContext.Result?.map(c => c.SIS) || []).includes(ar.Course)).length > 0;

  const filterCourses = useCallback(() => {
    if (coursesContext.Result) {
      let newFilteredCourses = [...coursesContext.Result];

      if (searchText.length > 2) {
        const fieldsToFilter = ["SIS", "Title", "Department", "Instructor"];
        newFilteredCourses = newFilteredCourses.filter(c => {
          let retVal = false;
          for (let i = 0; i < fieldsToFilter.length; i++) {
            const fieldToFilter = fieldsToFilter[i];
            if ((c as any)[fieldToFilter]) {
              retVal = (c as any)[fieldToFilter].toLowerCase().indexOf(searchText.toLowerCase()) > -1;
              if (retVal === true) {
                break;
              }
            }
          }
          return retVal;
        });
      }
      if (selectedInstructor) {
        newFilteredCourses = newFilteredCourses.filter(c => {
          return c.Instructor === selectedInstructor;
        });
      }

      if (selectedMyCourses && authContext.Result?.Courses && authContext.Result?.Courses.length > 0) {
        newFilteredCourses = isMyCourse(authContext.Result, newFilteredCourses)
      }

      if (showOnlyGraduateCourses) {
        newFilteredCourses = newFilteredCourses.filter(c => isCourseGraduateLevel(c));
      }

      if(_shouldRenderAccreditingColumns && !authContext?.Result?.KeplerAuthStatus?.isKeplerAdministrator)
      {
        let distinctDefintions: IAccreditorRuleFieldDefinition[] = [];
        accreditorRulesContext?.Result?.forEach(rule => {
          const def = rule.AccreditorRuleDefinition;
          if (!distinctDefintions.map(d => d.AccreditationFieldDefinitionId).includes(def.AccreditationFieldDefinitionId)) {
            distinctDefintions.push(def);
          }
        })
      
        newFilteredCourses = newFilteredCourses.filter(c => {
          const matchingRule = accreditorRulesContext.Result?.find(rule => distinctDefintions.map(d => d.AccreditationFieldDefinitionId).includes(rule.AccreditorRuleDefinition.AccreditationFieldDefinitionId) && c.SIS === rule.Course);
          return matchingRule && matchingRule.Criteria;
        })
      }

      newFilteredCourses.sort(_sortCourses);

      setFilteredCourses([...newFilteredCourses]);
    }
  }, [authContext.Result, coursesContext.Result, searchText, selectedInstructor, selectedMyCourses, showOnlyGraduateCourses]);

  const _sortCourses = ((a: ICourse, b: ICourse) => {
    if(a.Department < b.Department) {
      return -1;
     }
     else if(a.Department > b.Department)
     {
      return 1;
     }
     else //department is equal
     {
      return a.SIS < b.SIS ? - 1: 1;
     }
  })

  const _getSemestersSorted = (): ISemester[] | undefined => {
    return semesterContext.Result && semesterContext.Result.sort((a, b) => a.Year > b.Year ? -1 : 1);
  }

  useEffect(() => {
    if (filterCourses) {
      filterCourses();
    }
  }, [searchText, selectedInstructor, selectedMyCourses, semesterContext, coursesContext, filterCourses]);

  const _getSemesterAsKey = (semester: ISemester) => `${semester.Year}-${semester.Semester}`;

  const _getCommandBarItems = (): ICommandBarItemProps[] => {

    if (semesterContext.Result && semesterContext.Result.length > 0) {
      const options = (_getSemestersSorted() || []).map(c => _getSemesterAsKey(c)).filter((v, i, a) => a.indexOf(v) === i).map(o => ({ key: o, text: o.split("-").join(" ") }));
      let retVal = [{
        key: "semesterPicker",
        commandBarButtonAs: () => {
          return <div style={{ display: "flex", alignItems: "center" }}>
            <Dropdown
              selectedKey={semesterContext.SelectedSemester && _getSemesterAsKey(semesterContext.SelectedSemester)}
              styles={{ root: { width: 180 }, title: { borderWidth: 0, fontSize: 18, fontWeight: 400 } }}
              options={options} onChange={(event, option) => {
                const semester = semesterContext.Result?.find(s => _getSemesterAsKey(s) === option?.key.toString());
                if (semester && semester !== semesterContext.SelectedSemester) {
                  setFilteredCourses(fakeCourses)
                  semesterContext.SetSelectedSemester(semester);
                  coursesContext.setSemester(semester);
                }
              }} /> </div>;
        }
      }];
      if (coursesContext.Result && coursesContext.Result.length > 0) {
        retVal.push({
          key: "search",
          commandBarButtonAs: () => {
            return <div style={{ marginLeft: 10, display: "flex", alignItems: "center" }}>
                <SearchBox
                  onClear={() => { setSearchText(""); }}
                  styles={{ root: { width: 400 } }}
                  underlined
                  disableAnimation
                  defaultValue={searchText}
                  onChange={debounce((_: ChangeEvent<HTMLInputElement> | undefined, newValue?: string | undefined) => setSearchText(newValue || ''), 350)}
                  placeholder={`Search ${semesterContext.SelectedSemester?.Semester} ${semesterContext.SelectedSemester?.Year} courses`} />
        
            </div>;
          }
        });
      }
      return retVal;
    }
    else {
      return [];
    }
  };

  const _renderAccreditorCriteriaBlock = (criteria: ICriteria, startingPoint: boolean, shouldShowCriteria?: boolean) => {
    let opacity = startingPoint ? 1 : .5;

    return <TooltipHost key={`tooltip-${criteria.CriteriaDefinitionId}`} content={criteria.Criteria + ((startingPoint && !shouldShowCriteria) ? " (Starting Point)" : "")}>
      <div key={`criteria-block-${criteria.CriteriaDefinitionId}`} className="criteria-block" style={{ opacity: opacity, backgroundColor: criteria.CriteriaColor }}>
        {startingPoint && !shouldShowCriteria && <div className="starting-point-dot"></div>}
        {shouldShowCriteria && <div style={{ marginTop: 4 }}>{criteria.Criteria}</div>}
      </div>
    </TooltipHost>
  }

  const _renderLegend = () => {

    return <Stack>
      <Stack horizontal horizontalAlign='center'>
        <Label>{authContext.Result?.KeplerAuthStatus.idTokenClaims.companyName} - Student Performance Criteria Matrix - {semesterContext.SelectedSemester?.Semester} {semesterContext.SelectedSemester?.Year}</Label>
      </Stack>
      <Stack horizontalAlign="center" horizontal tokens={{ childrenGap: 6 }}>
        {accreditorCriteriaDefinitions.map(criteria => {
          return <>
            {_renderAccreditorCriteriaBlock(criteria, true, true)}
          </>
        })}
        <TooltipHost content="Starting Point">
          <div className="criteria-block" style={{ opacity: 1, backgroundColor: "white" }}>
            <Stack horizontal tokens={{ childrenGap: 6 }} style={{ marginTop: 4 }}><div>Starting Point</div><div style={{ marginTop: 8, marginLeft: 15 }} className="starting-point-dot"></div></Stack>
          </div>
        </TooltipHost>
      </Stack></Stack>

  }

  const _getCommandBarFarItems = (): ICommandBarItemProps[] => {
    if (semesterContext.Result && coursesContext.Result && coursesContext.Result.length > 0) {
      let retVal: ICommandBarItemProps[] = [];
      if (authContext.Result && (isCanvasStudent(authContext.Result) || isCanvasTeacher(authContext.Result)) && (authContext.Result?.Courses && authContext.Result?.Courses.length > 0)) {
        retVal.push({
          key: "mycourses",
          text: "My Courses",
          iconProps: { iconName: "CalendarWorkWeek", styles: { root: selectedMyCourses && styles.commandBarButtonRootChecked } },
          buttonStyles: { root: styles.commandBarButtonRoot, rootDisabled: styles.commandBarButtonRoot, rootChecked: styles.commandBarButtonRootChecked, iconChecked: styles.commandBarButtonRootChecked },
          checked: selectedMyCourses,
          onClick: () => {
            setSelectedMyCourses(!selectedMyCourses);
          }
        });
      }
      retVal = retVal.concat([
        {
          key: "isGraduate",
          text: "Graduate Level",
          iconProps: { iconName: "Education", styles: { root: showOnlyGraduateCourses && styles.commandBarButtonRootChecked } },
          buttonStyles: { root: styles.commandBarButtonRoot, rootDisabled: styles.commandBarButtonRoot, rootChecked: styles.commandBarButtonRootChecked, iconChecked: styles.commandBarButtonRootChecked },        
          commandBarButtonAs: () => {
            return <div style={{ marginLeft: 10, display: "flex", alignItems: "center" }}>
                <Stack horizontal tokens={{childrenGap: 6}}>                  
                    <Label style={{fontWeight: 400}}>{showOnlyGraduateCourses ? "Slide to show all courses" : "Slide to view graduate courses only"}</Label>
                    <TooltipHost content={showOnlyGraduateCourses ? "Showing only graduate level courses" : "Showing all courses"}>                 
                      <Toggle checked={showOnlyGraduateCourses} styles={{root: {marginTop: 4}}} onChange={(_ev, checked) => {
                      setShowOnlyGraduateCourses(!showOnlyGraduateCourses);
                    }} />
                  </TooltipHost>
                </Stack>
              </div>
          
          }
        },
        {
          key: "instructor",
          text: "Instructor",
          buttonStyles: { root: styles.commandBarButtonRoot, rootDisabled: styles.commandBarButtonRoot },
          commandBarButtonAs: () => {
            return <Stack horizontal>
              <div style={{ marginLeft: 10, display: "flex", alignItems: "center" }}>
                <Icon iconName="Education" styles={{ root: { marginRight: 2 } }} />
                <Dropdown
                  placeholder="Instructor"
                  styles={{ root: { width: 200 }, title: { borderWidth: 0 }, callout: { width: "200 !important" }, dropdownItemsWrapper: { width: 200 } }}
                  selectedKey={selectedInstructor}
                  options={filteredCourses.map(c => c.Instructor).sort((a, b) => a > b ? 1 : -1).filter((v, i, a) => a.indexOf(v) === i).map(c => ({ key: c, text: c }))}
                  onChange={(event, option) => {
                    setSelectedInstructor(option?.key?.toString());
                  }}
                />
              </div>
              {selectedInstructor && <div style={{ marginLeft: 10, display: "flex", alignItems: "center" }}>
                <IconButton iconProps={{ iconName: "Clear" }} onClick={() => {
                  setSelectedInstructor(undefined);
                }} />
              </div>}
            </Stack>;
          }
        }]);
      return retVal;
    }
    else {
      return []
    }
  };

  const _onRulesPanelClosed = () => {
    setShowRulesPanelItem(undefined);
  }

  const _getColumns = (): IColumn[] => {
    let retVal: IColumn[] = [
      {
        key: "courseId",
        name: "Course ID",
        minWidth: 150,
        maxWidth: 300,
        isResizable: true,
        fieldName: "SIS"
      },
      authContext.Result?.KeplerAuthStatus?.isKeplerAdministrator && {
        key: "editRules",
        name: "Edit Rules",
        minWidth: 80,
        maxWidth: 80,
        isResizable: true,
        fieldName: "Rules",
        onRender: (item) => {
          return <TooltipHost content="Edit accreditation rules">
            <IconButton iconProps={{ iconName: "PageHeaderEdit" }} onClick={(ev) => {
              ev.stopPropagation();
              ev.preventDefault();
              setShowRulesPanelItem(item);
            }} /></TooltipHost>
        }
      },
      {
        key: "courseTitle",
        name: "Title",
        minWidth: 250,
        maxWidth: 400,
        isResizable: true,
        isMultiline: true,
        fieldName: "Title"
      },
      (!accreditorRulesContext.Result || accreditorRulesContext.Result.length === 0) && {
        key: "instructor",
        name: "Instructor",
        minWidth: 100,
        maxWidth: 200,
        isResizable: true,
        fieldName: "Instructor",
        onRender: (item: ICourse) => {
          return <TooltipHost content={item.Instructor}>{item.Instructor}</TooltipHost>
        }
      },
      {
        key: "isGraduate",
        name: "Graduate Level",
        minWidth: 135,
        maxWidth: 135,
        isResizable: true,
        onRender: (item: ICourse) => {
          return isCourseGraduateLevel(item) ? "Yes" : "No"
        }
      }/*,
      {
        key: "semesterYear",
        name: "Year",
        minWidth: 55,
        maxWidth: 55,
        onRender: (item: ICourse) => {
          return item.Year
        }
      },
      {
        key: "semester",
        name: "Semester",
        minWidth: 80,
        maxWidth: 80,
        onRender: (item: ICourse) => {
          return item.Semester
        }
      }*/
    ];

    if (accreditorRulesContext.Result && _shouldRenderAccreditingColumns) {
      let distinctDefintions: IAccreditorRuleFieldDefinition[] = [];

      accreditorRulesContext.Result.forEach(rule => {
        const def = rule.AccreditorRuleDefinition;
        if (!distinctDefintions.map(d => d.AccreditationFieldDefinitionId).includes(def.AccreditationFieldDefinitionId)) {
          distinctDefintions.push(def);
        }
      })

      distinctDefintions.forEach(definition => {
        retVal.push({
          key: `accredit-rule-${definition.AccreditationFieldDefinitionId}`,
          name: getAccreditationRuleDefinitionLabel(definition),
          minWidth: 100,
          maxWidth: 200,
          data: {
            isRule: true,
            ruleDefinition: definition
          },
          onRender: (item: ICourse) => {
            const matchingRule = accreditorRulesContext.Result?.find(rule => rule.AccreditorRuleDefinition.AccreditationFieldDefinitionId === definition.AccreditationFieldDefinitionId && item.SIS === rule.Course);
            if (matchingRule && matchingRule.Criteria) {
              return _renderAccreditorCriteriaBlock(matchingRule.Criteria, matchingRule.IsStartingPoint);
            }
          }
        })
      })
    }
    return retVal.filter(r => r);
  };

  const _renderCustomListHeader = (detailsHeaderProps?: IDetailsHeaderProps, defaultRender?: IRenderFunction<IDetailsHeaderProps>) => {
    return <Sticky isScrollSynced={true}>
      {
        detailsHeaderProps && defaultRender && (defaultRender!({
          ...detailsHeaderProps,
          onRenderColumnHeaderTooltip: (tooltipHostProps: IDetailsColumnRenderTooltipProps | undefined) => {
            const column = tooltipHostProps?.column;
            if (column && column.data?.isRule && column.data?.ruleDefinition) {
              const ruleDefinition = column.data.ruleDefinition as IAccreditorRuleFieldDefinition;
              return <div className='rule-header' style={{ backgroundColor: column.data.ruleDefinition.CompetencyColor }}>
                <TooltipHost content={getAccreditationRuleDefinitionLabel(ruleDefinition)}>
                  <div className='rule-category-header'>{ruleDefinition.Category}</div>
                  {ruleDefinition.Competency && <div className='rule-competency'>{ruleDefinition.Competency}</div>}
                </TooltipHost>
              </div>
            }
            return <span title={column?.name}>{tooltipHostProps?.children}</span>
          }
        })
        )
      }
    </Sticky>
  }

  const _getGroups = (items: ICourse[]): IGroup[] => {
    let groups: any = {};
    items.sort(_sortCourses);    
    items.forEach((item, index) => {
        let group: number = groups[item.Department] || 0;
        group = group + 1;
        groups[item.Department] = group;
      });
    let index = 0;
    return Object.keys(groups).map((g, i) => {
      const retVal: IGroup = {
        key: `group-${i}-${g}`,
        name: g,
        startIndex: index,
        count: groups[g]
      };
      index = retVal.startIndex + retVal.count;
      return retVal;
    });
  };

  let className = "courses";
  if (authContext.Result?.KeplerAuthStatus && accreditorRulesContext.Result && accreditorRulesContext.Result.length > 0) {
    className += " accreditor-courses"
  }

  return (
    <div className={className}>
      <Stack horizontal style={{ height: "100%" }}>
        <LeftNav />
        <Stack.Item grow>
          {semesterContext.Pending && <ProgressIndicator styles={{ root: { textAlign: "center" } }} label={`Loading semesters${navigatingDirectlyToCourse ? " and navigating to course" : ""}`} />}
          {!navigatingDirectlyToCourse && !semesterContext.Error && semesterContext.Result && <CommandBar styles={{ root: styles.commandBarRoot }} items={_getCommandBarItems()} farItems={_getCommandBarFarItems()} />}
          {semesterContext.Error && <MessageBar messageBarType={MessageBarType.error}>{JSON.stringify(semesterContext.Error)}</MessageBar>}
          {semesterContext.Result && semesterContext.Result.length === 0 && <>There are no semseters found</>}
          {!semesterContext.Error && semesterContext.Result && semesterContext.SelectedSemester && <>
            {coursesContext.Pending && <ProgressIndicator styles={{ root: { textAlign: "center" } }} label={`Loading ${semesterContext.SelectedSemester.Semester} ${semesterContext.SelectedSemester.Year} courses`} />}
            {showRulesPanelItem && <AccreditationRulesPanel course={showRulesPanelItem} onClose={_onRulesPanelClosed} onRefresh={() => {
              accreditorRulesContext.SetRefreshFlag(!refreshFlag.current)
              refreshFlag.current = !refreshFlag.current;
            }} />}
            {_shouldRenderAccreditingColumns && _renderLegend()}
            {navigatingDirectlyToCourse ? <ProgressIndicator styles={{ root: { textAlign: "center" } }} label={"Navigating to course"} /> : <ShimmeredDetailsList
              styles={{ root: { cursor: 'pointer !important' } }}
              onActiveItemChanged={(course: ICourse, index, ev) => {
                if (ev?.target.getAttribute("type") !== 'button') {
                  navigate(`/courses/${course.Year}/${course.Semester}/${encodeURIComponent(course.SIS || course.Course)}`);
                }
              }}
              enableShimmer={coursesContext.Pending || navigatingDirectlyToCourse}
              onRenderDetailsHeader={_renderCustomListHeader}
              layoutMode={DetailsListLayoutMode.fixedColumns}
              groups={coursesContext.Pending ? undefined : (coursesContext.Error ? [] : _getGroups([...filteredCourses]))}
              items={coursesContext.Error ? [] : [...filteredCourses]}
              selectionMode={SelectionMode.none}
              columns={_getColumns()}
            />}
            {!coursesContext.Pending && !coursesContext.Error && coursesContext.Result && coursesContext.Result?.length === 0 && <div className='section'>There are no courses available for {semesterContext.SelectedSemester.Semester} {semesterContext.SelectedSemester.Year}. Select another semseter from the dropdown to continue</div>}</>}

          {!coursesContext.Pending && coursesContext.Error && <MessageBar messageBarType={MessageBarType.error}>{typeof coursesContext.Error === "object" ? JSON.stringify(coursesContext.Error) : coursesContext.Error}</MessageBar>}
        </Stack.Item>
      </Stack>
    </div>
  );
};

export default Courses;
